import { z } from "zod/v4";
import { SiteIdCodec } from "@visioni/a3s-schema/codecs";

/**
 * The product the user is expressing interest in upgrading to. New product
 * lines are added by extending the enum — the rest of the upsell pipeline
 * treats this value as opaque, so the only coupling is here and in the
 * sales-side HubSpot Lead property mapping.
 */
export const ProductInterest = z.enum(["monitoring"]);
export type ProductInterest = z.infer<typeof ProductInterest>;

/**
 * Body of `POST /upsell/request`. The caller's email, name, tenant, and
 * enrichment data are derived server-side from the auth token and A3S
 * stores — we only accept the product interest and optional conversion
 * page from the client to avoid spoofing.
 */
export const UpsellRequest = z.object({
  productInterest: ProductInterest,
  siteId: SiteIdCodec.optional(),
  /** The app page where the user clicked the upsell CTA (e.g. "/alarms"). */
  conversionPage: z.string().optional(),
});
export type UpsellRequest = z.infer<typeof UpsellRequest>;

/**
 * Product-dependent params for `GET /upsell/status`.
 *
 * Each product can require different context to compute "is it already
 * active". Monitoring is per-site, so it requires a `siteId`. Future
 * products may key off a different scope (per-tenant, per-asset, etc.) —
 * adding one means appending a new branch to this discriminated union.
 *
 * The handler uses this schema as the authoritative validation; the
 * HTTP-level query schema in `client-messages/upsell.ts` keeps `siteId`
 * optional so query-string parsing accepts the flat shape, and the
 * handler narrows via this union and returns 400 if a required field
 * for the requested product is missing.
 */
export const UpsellStatusParams = z.discriminatedUnion("product", [
  z.object({ product: z.literal("monitoring"), siteId: z.string() }),
]);
export type UpsellStatusParams = z.infer<typeof UpsellStatusParams>;

/**
 * Per-user, per-product status.
 *
 * `upsellStatus` reflects the open-Lead state in HubSpot: `pending` if
 * an open Lead exists for the user's Contact + product (the sales team
 * has been notified), `none` otherwise (the user can submit a new
 * request).
 *
 * `isProductAlreadyActive` is independent of the Lead state and is
 * resolved from the platform's source of truth for the product (e.g.
 * for `monitoring`, the hub event's `asset_hire.monitoring` array
 * evaluated by `findActiveMonitoringEntry`). It is `true` when the
 * customer already has the product active in the relevant scope. The
 * banner UI uses this to switch between "monitoring active" vs the
 * upsell CTA. The two flags are not mutually exclusive — a customer
 * can have an active contract and an open renewal Lead at the same
 * time.
 */
export const UpsellStatus = z.object({
  productInterest: ProductInterest,
  upsellStatus: z.enum(["none", "pending"]),
  isProductAlreadyActive: z.boolean(),
});
export type UpsellStatus = z.infer<typeof UpsellStatus>;

/**
 * Response of `GET /upsell/status`. Returns a status for every product
 * interest the user can request — this lets the app render multiple
 * upsell surfaces from a single fetch.
 */
export const UpsellStatusResponse = z.object({
  statuses: z.array(UpsellStatus),
});
export type UpsellStatusResponse = z.infer<typeof UpsellStatusResponse>;
